import { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AdminShell, Button, type NavGroup } from "@wmart/ui";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { ToastProvider } from "./lib/toast.js";
import { useApplicationsBadge } from "./lib/use-applications-badge.js";
import { useQuotesBadge } from "./lib/use-quotes-badge.js";
import { Login } from "./pages/Login.js";
import { PasswordResetConfirm } from "./pages/PasswordResetConfirm.js";
import { Dashboard } from "./pages/Dashboard.js";
import { ProductsList } from "./pages/ProductsList.js";
import { ProductEdit } from "./pages/ProductEdit.js";
import { CategoriesTree } from "./pages/CategoriesTree.js";
import { BrandsList } from "./pages/BrandsList.js";
import { SuppliersList } from "./pages/SuppliersList.js";
import { CustomersList } from "./pages/CustomersList.js";
import { CustomerDetail } from "./pages/CustomerDetail.js";
import { TeamList } from "./pages/TeamList.js";
import { TeamEdit } from "./pages/TeamEdit.js";
import { CompaniesList } from "./pages/CompaniesList.js";
import { CompanyEdit } from "./pages/CompanyEdit.js";
import { CompanyLocationEdit } from "./pages/CompanyLocationEdit.js";
import { OrdersList } from "./pages/OrdersList.js";
import { OrderDetail } from "./pages/OrderDetail.js";
import { OrderCreate } from "./pages/OrderCreate.js";
import { ApplicationsList } from "./pages/ApplicationsList.js";
import { ApplicationDetail } from "./pages/ApplicationDetail.js";
import { InvoicesList } from "./pages/InvoicesList.js";
import { InvoiceDetail } from "./pages/InvoiceDetail.js";
import { MergeInvoicesWizard } from "./pages/MergeInvoicesWizard.js";
import { QuoteList } from "./routes/quotes/QuoteList.js";
import { QuoteDetail } from "./routes/quotes/QuoteDetail.js";
import { ExtractionsList } from "./pages/ExtractionsList.js";
import { NewExtraction } from "./pages/NewExtraction.js";
import { ExtractionDetail } from "./pages/ExtractionDetail.js";
import { SupplierPoList } from "./pages/supplier/SupplierPoList.js";
import { SupplierPoDetail } from "./pages/supplier/SupplierPoDetail.js";

const ReportsHub = lazy(() =>
  import("./pages/reports/ReportsHub.js").then((m) => ({ default: m.ReportsHub }))
);

/**
 * Page-title resolver. Order-sensitive: longest-prefix wins so
 * `/customers/:id` resolves to "Customers" not "Dashboard". The catch-all
 * "Admin" preserves the topbar typography when a future route is added.
 */
const TITLE_MATCHERS: Array<{ test: (pathname: string) => boolean; title: string }> = [
  { test: (p) => p === "/" || p === "/dashboard", title: "Dashboard" },
  // Supplier portal — MUST precede /orders so /supplier/orders does not
  // resolve to the staff "Orders" title (it would not match anyway since
  // /orders is a startsWith, but keeping the supplier matcher first is the
  // defensive, order-insensitive choice).
  { test: (p) => p.startsWith("/supplier/orders"), title: "Purchase orders" },
  { test: (p) => p.startsWith("/orders"), title: "Orders" },
  { test: (p) => p.startsWith("/quotes"), title: "Quotes" },
  { test: (p) => p.startsWith("/customers"), title: "Customers" },
  { test: (p) => p.startsWith("/team"), title: "Team" },
  { test: (p) => p.startsWith("/products"), title: "Products" },
  { test: (p) => p.startsWith("/categories"), title: "Categories" },
  { test: (p) => p.startsWith("/brands"), title: "Brands" },
  { test: (p) => p.startsWith("/suppliers"), title: "Suppliers" },
  { test: (p) => p.startsWith("/companies"), title: "Companies" },
  { test: (p) => p.startsWith("/applications"), title: "Applications" },
  { test: (p) => p.startsWith("/invoices"), title: "Invoices" },
  { test: (p) => p.startsWith("/reports"), title: "Reports" },
  { test: (p) => p.startsWith("/catalog"), title: "Catalog" },
];

function resolvePageTitle(pathname: string): string {
  return TITLE_MATCHERS.find((m) => m.test(pathname))?.title ?? "Admin";
}

/**
 * Auth-gated shell. Wraps every authenticated route in `<AdminShell>` from
 * `@wmart/ui` with 5 nav groups; loading state shows a plain-page spinner;
 * anon + non-staff session bounces to /login (preserved verbatim from the
 * pre-T10 Shell). The new-order CTA in the topbar links to /orders/new (the
 * admin telesales order builder).
 */
function ShellWithNav({ children }: { children: React.ReactNode }) {
  const { me, status, signOut } = useAuth();
  const loc = useLocation();
  const { count: applicationsBadge } = useApplicationsBadge();
  const { count: quotesBadge } = useQuotesBadge();

  if (status === "loading") {
    return <p className="p-8 text-body-sm text-ink-500">Loading…</p>;
  }
  if (status === "anon") {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }
  // Customer-session users pass the auth gate but admin endpoints 403.
  // Bounce them to /login so they can sign in as staff (P08 Plan §1856
  // — preserved verbatim from the previous Shell).
  if (me && me.kind !== "staff") {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }

  const pathname = loc.pathname;
  // Team (staff management) is wmart_admin-only on the API. We hide the nav
  // entry for non-admins as a UX nicety; the server @RequireRole guard remains
  // the source of truth (a non-admin who navigates directly gets a 403 toast).
  const isWmartAdmin = me?.staff?.role === "wmart_admin";
  const navGroups: NavGroup[] = [
    {
      label: "Operations",
      items: [
        {
          label: "Dashboard",
          href: "/dashboard",
          icon: "user",
          active: pathname === "/" || pathname === "/dashboard",
        },
        {
          label: "Orders",
          href: "/orders",
          icon: "package",
          active: pathname.startsWith("/orders"),
        },
        {
          // Spec §9.2: Quotes sits between Orders and Customers in the
          // operator priority list. We keep it in the Operations group
          // adjacent to Orders since it is the buyer-initiated cousin
          // of the order queue.
          label: (
            <span className="flex items-center justify-between gap-2 w-full">
              Quotes
              {quotesBadge > 0 ? (
                <span
                  aria-label={`${quotesBadge} pending`}
                  className="inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent px-1 text-caption font-semibold text-paper-warm"
                >
                  {quotesBadge}
                </span>
              ) : null}
            </span>
          ),
          href: "/quotes",
          icon: "fileText",
          active: pathname.startsWith("/quotes"),
        },
        {
          label: "Invoices",
          href: "/invoices",
          icon: "fileText",
          active: pathname.startsWith("/invoices"),
        },
        {
          label: (
            <span className="flex items-center justify-between gap-2 w-full">
              Applications
              {applicationsBadge > 0 ? (
                <span
                  aria-label={`${applicationsBadge} pending`}
                  className="inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent px-1 text-caption font-semibold text-paper-warm"
                >
                  {applicationsBadge}
                </span>
              ) : null}
            </span>
          ),
          href: "/applications",
          icon: "user",
          active: pathname.startsWith("/applications"),
        },
        {
          label: "Customers",
          href: "/customers",
          icon: "user",
          active: pathname.startsWith("/customers"),
        },
        ...(isWmartAdmin
          ? [
              {
                label: "Team",
                href: "/team",
                icon: "user" as const,
                active: pathname.startsWith("/team"),
              },
            ]
          : []),
      ],
    },
    {
      label: "Catalog",
      items: [
        {
          label: "Products",
          href: "/products",
          icon: "package",
          active: pathname.startsWith("/products"),
        },
        {
          label: "Categories",
          href: "/categories",
          icon: "filter",
          active: pathname.startsWith("/categories"),
        },
        {
          label: "Brands",
          href: "/brands",
          icon: "package",
          active: pathname.startsWith("/brands"),
        },
        {
          label: "Suppliers",
          href: "/suppliers",
          icon: "package",
          active: pathname.startsWith("/suppliers"),
        },
        {
          label: "Catalogue imports",
          href: "/catalog/extractions",
          icon: "fileText",
          active: pathname.startsWith("/catalog/extractions"),
        },
      ],
    },
    {
      label: "Companies",
      items: [
        {
          label: "Companies",
          href: "/companies",
          icon: "user",
          active: pathname.startsWith("/companies"),
        },
      ],
    },
    {
      label: "Analytics",
      items: [
        {
          label: "Reports",
          href: "/reports",
          icon: "fileText",
          active: pathname.startsWith("/reports"),
        },
      ],
    },
  ];

  return (
    <AdminShell
      pageTitle={resolvePageTitle(pathname)}
      navGroups={navGroups}
      newOrderHref="/orders/new"
      rightSlot={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void signOut()}
          aria-label={
            me?.email ? `Sign out (${me.email})` : "Sign out"
          }
        >
          Sign out
        </Button>
      }
    >
      {children}
    </AdminShell>
  );
}

/**
 * Supplier-portal shell. Mirrors `ShellWithNav` but inverted: it admits ONLY
 * supplier-kind sessions. Loading shows nothing (the inner pages render their
 * own "Loading…"); anon → /login; any non-supplier session (or a supplier
 * session missing the `supplier` block) bounces to /login. The single nav
 * group is "Purchase orders"; the topbar title is the supplier's name. No
 * new-order CTA, no staff badges/density extras beyond the shared shell
 * defaults.
 */
function SupplierShellWithNav({ children }: { children: React.ReactNode }) {
  const { me, status, signOut } = useAuth();
  const loc = useLocation();

  if (status === "loading") {
    return null;
  }
  if (status === "anon") {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }
  // Staff/customer sessions that somehow reach a /supplier route bounce to
  // /login (the inverse of ShellWithNav's staff gate). The server also scopes
  // every /v1/supplier/* endpoint to supplier sessions, so this is a UX gate.
  if (!me || me.kind !== "supplier" || !me.supplier) {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }

  const pathname = loc.pathname;
  const navGroups: NavGroup[] = [
    {
      label: "Purchase orders",
      items: [
        {
          label: "Purchase orders",
          href: "/supplier/orders",
          icon: "package",
          active: pathname.startsWith("/supplier/orders"),
        },
      ],
    },
  ];

  return (
    <AdminShell
      pageTitle={me.supplier.supplierName}
      navGroups={navGroups}
      rightSlot={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void signOut()}
          aria-label={me.email ? `Sign out (${me.email})` : "Sign out"}
        >
          Sign out
        </Button>
      }
    >
      {children}
    </AdminShell>
  );
}

/**
 * Landing redirect for `/`. Suppliers go to their PO queue; everyone else
 * (staff, anon-bounced-to-login downstream) goes to the staff dashboard. We
 * read auth synchronously: while loading we render nothing to avoid a
 * dashboard→portal flash for suppliers.
 */
function IndexRedirect() {
  const { me, status } = useAuth();
  if (status === "loading") return null;
  if (me?.kind === "supplier") {
    return <Navigate to="/supplier/orders" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

/**
 * Catch-all (`*`) redirect — same shape as IndexRedirect. Supplier-aware so
 * a supplier hitting an unknown path lands directly on the portal instead of
 * bouncing through /dashboard → ShellWithNav's staff gate → /login.
 */
function CatchAllRedirect() {
  const { me, status } = useAuth();
  if (status === "loading") return null;
  if (me?.kind === "supplier") {
    return <Navigate to="/supplier/orders" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<IndexRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/supplier/orders"
            element={
              <SupplierShellWithNav>
                <SupplierPoList />
              </SupplierShellWithNav>
            }
          />
          <Route
            path="/supplier/orders/:id"
            element={
              <SupplierShellWithNav>
                <SupplierPoDetail />
              </SupplierShellWithNav>
            }
          />
          <Route
            path="/password-reset/confirm"
            element={<PasswordResetConfirm />}
          />
          <Route
            path="/dashboard"
            element={
              <ShellWithNav>
                <Dashboard />
              </ShellWithNav>
            }
          />
          <Route
            path="/products"
            element={
              <ShellWithNav>
                <ProductsList />
              </ShellWithNav>
            }
          />
          <Route
            path="/products/new"
            element={
              <ShellWithNav>
                <ProductEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/products/:id"
            element={
              <ShellWithNav>
                <ProductEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/categories"
            element={
              <ShellWithNav>
                <CategoriesTree />
              </ShellWithNav>
            }
          />
          <Route
            path="/brands"
            element={
              <ShellWithNav>
                <BrandsList />
              </ShellWithNav>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ShellWithNav>
                <SuppliersList />
              </ShellWithNav>
            }
          />
          {/* /catalog/extractions/new MUST precede /catalog/extractions/:id so the
              literal segment wins over the dynamic param. */}
          <Route
            path="/catalog/extractions/new"
            element={
              <ShellWithNav>
                <NewExtraction />
              </ShellWithNav>
            }
          />
          <Route
            path="/catalog/extractions"
            element={
              <ShellWithNav>
                <ExtractionsList />
              </ShellWithNav>
            }
          />
          {/* /catalog/extractions/:id — D2 Review Queue detail page. */}
          <Route
            path="/catalog/extractions/:id"
            element={
              <ShellWithNav>
                <ExtractionDetail />
              </ShellWithNav>
            }
          />
          <Route
            path="/customers"
            element={
              <ShellWithNav>
                <CustomersList />
              </ShellWithNav>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ShellWithNav>
                <CustomerDetail />
              </ShellWithNav>
            }
          />
          {/* /team/new MUST precede /team/:id so the literal segment wins. */}
          <Route
            path="/team/new"
            element={
              <ShellWithNav>
                <TeamEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/team"
            element={
              <ShellWithNav>
                <TeamList />
              </ShellWithNav>
            }
          />
          <Route
            path="/team/:id"
            element={
              <ShellWithNav>
                <TeamEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/companies"
            element={
              <ShellWithNav>
                <CompaniesList />
              </ShellWithNav>
            }
          />
          <Route
            path="/companies/new"
            element={
              <ShellWithNav>
                <CompanyEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/companies/:id"
            element={
              <ShellWithNav>
                <CompanyEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/companies/:id/locations/new"
            element={
              <ShellWithNav>
                <CompanyLocationEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/companies/:id/locations/:locId"
            element={
              <ShellWithNav>
                <CompanyLocationEdit />
              </ShellWithNav>
            }
          />
          <Route
            path="/orders"
            element={
              <ShellWithNav>
                <OrdersList />
              </ShellWithNav>
            }
          />
          {/* /orders/new MUST precede /orders/:id so the literal segment wins
              over the dynamic param. */}
          <Route
            path="/orders/new"
            element={
              <ShellWithNav>
                <OrderCreate />
              </ShellWithNav>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ShellWithNav>
                <OrderDetail />
              </ShellWithNav>
            }
          />
          <Route
            path="/quotes"
            element={
              <ShellWithNav>
                <QuoteList />
              </ShellWithNav>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ShellWithNav>
                <QuoteDetail />
              </ShellWithNav>
            }
          />
          <Route
            path="/applications"
            element={
              <ShellWithNav>
                <ApplicationsList />
              </ShellWithNav>
            }
          />
          <Route
            path="/applications/:id"
            element={
              <ShellWithNav>
                <ApplicationDetail />
              </ShellWithNav>
            }
          />
          <Route
            path="/invoices"
            element={
              <ShellWithNav>
                <InvoicesList />
              </ShellWithNav>
            }
          />
          {/* /invoices/merge MUST precede /invoices/:id so the literal
              segment wins over the dynamic param (react-router declaration
              order matters when both routes are static-vs-dynamic at the
              same depth). */}
          <Route
            path="/invoices/merge"
            element={
              <ShellWithNav>
                <MergeInvoicesWizard />
              </ShellWithNav>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ShellWithNav>
                <InvoiceDetail />
              </ShellWithNav>
            }
          />
          <Route
            path="/reports"
            element={
              <ShellWithNav>
                <Suspense fallback={null}>
                  <ReportsHub />
                </Suspense>
              </ShellWithNav>
            }
          />
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}
